import { GroupUserRole, Web3Address } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { IAccount } from 'modules/common/types';
import { RootState } from 'store';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { selectIsWeb3Auth } from 'modules/auth/store/selectors';
import {
  selectIsWeb3OAuth,
  selectUserAddress,
} from 'modules/user/store/selectors';

import { PERSONAL_ACCOUNT_NAME } from '../constants';
import {
  selectDevdaoAccountJWT,
  selectDevdaoAccountJWTLoading,
  selectDevdaoAccountJWTState,
} from '../actions/fetchDevdaoAccountJWT';
import {
  selectIsDevdaoAccount,
  selectIsDevdaoAccountLoading,
} from '../actions/fetchIsDevdaoAccount';
import {
  selectIsOldAccount,
  selectIsOldAccountLoading,
} from '../actions/fetchIsOldAccount';
import {
  selectOAuthAccountJWT,
  selectOAuthAccountJWTLoading,
  selectOAuthAccountJWTState,
} from '../actions/fetchOAuthAccountJWT';
import {
  selectOldAccountJWT,
  selectOldAccountJWTLoading,
  selectOldAccountJWTState,
} from '../actions/fetchOldAccountJWT';
import {
  selectStandardAccountJWT,
  selectStandardAccountJWTLoading,
  selectStandardAccountJWTState,
} from '../actions/fetchStandardAccountJWT';

export const selectPersonalAccountSlice = (state: RootState) =>
  state.personalAccount;

export const selectPersonalAccountUserEndpointToken = createSelector(
  selectPersonalAccountSlice,
  state => state.userEndpointToken,
);

export const selectPersonalAccountWorkerToken = createSelector(
  selectPersonalAccountSlice,
  state => state.workerToken,
);

export const selectIsPersonalAccountInitialized = createSelector(
  selectPersonalAccountUserEndpointToken,
  selectPersonalAccountWorkerToken,
  (userEndpointToken, workerToken) => Boolean(userEndpointToken && workerToken),
);

export const selectPersonalAccount = createSelector(
  selectUserAddress,
  address => {
    if (address) {
      const personalAccount: IAccount = {
        address,
        index: 0,
        name: PERSONAL_ACCOUNT_NAME,
        role: GroupUserRole.owner,
      };

      return personalAccount;
    }

    return undefined;
  },
);

export const selectPersonalAccountJWT = createSelector(
  (state: RootState) => selectIsDevdaoAccount(state),
  (state: RootState) => selectDevdaoAccountJWT(state, { params: undefined }),
  (state: RootState, authAddress: Web3Address) =>
    selectIsOldAccount(state, { account: authAddress }),
  (state: RootState, authAddress: Web3Address) =>
    selectOldAccountJWT(state, { account: authAddress }),
  (state: RootState) => selectStandardAccountJWT(state, { params: undefined }),
  (state: RootState) => selectOAuthAccountJWT(state, { params: undefined }),
  (state: RootState) => selectIsWeb3Auth(state),
  selectIsWeb3OAuth,
  (
    isDevdaoAccount,
    devdaoAccountJWT,
    isOldAccount,
    oldAccountJWT,
    standardAccountJWT,
    oAuthAccountJWT,
    isWeb3Auth,
    isWeb3OAuth,
    // eslint-disable-next-line max-params
  ) => {
    // web3 devdao users
    if (isDevdaoAccount) {
      return devdaoAccountJWT;
    }

    // web3 old users
    if (isOldAccount) {
      return oldAccountJWT;
    }

    // standard web3 users
    if (isWeb3Auth || isWeb3OAuth) {
      return standardAccountJWT;
    }

    // oauth users
    return oAuthAccountJWT;
  },
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPersonalAccountJWTLoading = createSelector(
  (state: RootState) => selectIsDevdaoAccountLoading(state),
  (state: RootState) =>
    selectDevdaoAccountJWTLoading(state, { params: undefined }),
  (state: RootState, authAddress: Web3Address) =>
    selectIsOldAccountLoading(state, { account: authAddress }),
  (state: RootState, authAddress: Web3Address) =>
    selectOldAccountJWTLoading(state, { account: authAddress }),
  (state: RootState) =>
    selectStandardAccountJWTLoading(state, { params: undefined }),
  (state: RootState) =>
    selectOAuthAccountJWTLoading(state, { params: undefined }),
  (
    isDevdaoAccountLoading,
    devdaoAccountJWTLoading,
    isOldAccountLoading,
    oldAccountJWTLoading,
    standardAccountJWTLoading,
    oAuthAccountJWTLoading,
    // eslint-disable-next-line max-params
  ) =>
    isDevdaoAccountLoading ||
    devdaoAccountJWTLoading ||
    isOldAccountLoading ||
    oldAccountJWTLoading ||
    standardAccountJWTLoading ||
    oAuthAccountJWTLoading,
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPersonalAccountJWTLoaded = createSelector(
  (state: RootState) =>
    selectDevdaoAccountJWTState(state, { params: undefined }).isSuccess,
  (state: RootState, authAddress: Web3Address) =>
    selectOldAccountJWTState(state, { account: authAddress }).isSuccess,
  (state: RootState) =>
    selectStandardAccountJWTState(state, { params: undefined }).isSuccess,
  (state: RootState) =>
    selectOAuthAccountJWTState(state, { params: undefined }).isSuccess,
  (
    devdaoAccountJWTLoaded,
    oldAccountJWTLoaded,
    standardAccountJWTLoaded,
    oAuthAccountJWTLoaded,
    // eslint-disable-next-line max-params
  ) =>
    devdaoAccountJWTLoaded ||
    oldAccountJWTLoaded ||
    standardAccountJWTLoaded ||
    oAuthAccountJWTLoaded,
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectIsPersonalAccountInitializing =
  selectPersonalAccountJWTLoading;
